import { useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ICollectionsReducerState } from '../../../modules/reducers';
import { DESIGN_COLOR_KEYS, presetsForTemplate } from '../../../modules/constants/colorPresets';
import { getDevelopedTemplates } from '../../../modules/selectors/templates';
import { getColorPresets } from '../../../modules/selectors/colorPresets';

type Design = ICollectionsReducerState['design'];
type Variables = { [key: string]: string };

/** Design keys that are persisted on their own, outside the colour-only path. */
const STANDALONE_DESIGN_KEYS = [
  'imageSpacing',
  'newUseCustomColors',
  'newColorPreset',
  'reactionSet',
  'iconSet'
] as const;

export const DEFAULT_VALUES = {
  galleryStyle: 'justified',
  disableDecorations: false
};

/** Colour writes are debounced so dragging the picker does not spam the API. */
const COLOR_SUBMIT_DELAY = 1500;

interface IFormValue {
  selectedTemplateId: string;
  design: Design;
  galleryStyle: string;
}

interface IArgs {
  formValue: IFormValue;
  setValue: (value: Partial<IFormValue>, isOnlyColor?: boolean) => void;
  submitColor?: (arg: any) => void;
  availableRules: { canCustomimzeDesign?: boolean };
  setUpgradeAccountOpenState: (obj: any) => void;
}

interface ITemplateConfigurationForm {
  selectTemplate: (selectedTemplateId: string) => void;
  setDesign: (design: Partial<Design>) => void;
  submitDesignColor: (colors: Variables) => void;
  changeGalleryStyle: (value: string) => void;
  toggleDecorations: (value: boolean) => void;
  toggleTrademark: (value: boolean) => void;
}

/** Picks out whichever of the redesign's colours an update carries. */
const pickColorVariables = (variables?: Variables): Variables => {
  if (!variables) {
    return {};
  }

  return DESIGN_COLOR_KEYS.reduce<Variables>((acc, key) => {
    if (typeof variables[key] === 'string') {
      acc[key] = variables[key];
    }

    return acc;
  }, {});
};

/**
 * Translates the design form's granular edits into `setValue` calls.
 *
 * Colour changes take a separate path: they are flagged `isOnlyColor` so the
 * parent only mirrors them locally while the picker is being dragged, and the
 * debounced `submitColor` is what actually persists them.
 */
export const useTemplateConfigurationForm = ({
  formValue,
  setValue,
  submitColor,
  availableRules,
  setUpgradeAccountOpenState
}: IArgs): ITemplateConfigurationForm => {
  const templates = useSelector(getDevelopedTemplates);
  const colorPresets = useSelector(getColorPresets);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  const cancelPendingColorSubmit = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const submitDebounced = useCallback(
    (data: Variables) => {
      cancelPendingColorSubmit();

      timer.current = setTimeout(() => {
        if (submitColor) {
          submitColor(data);
        }
      }, COLOR_SUBMIT_DELAY);
    },
    [cancelPendingColorSubmit, submitColor]
  );

  /** Blocked plans get the upgrade modal instead of the change. */
  const isBlocked = useCallback(
    (modalData?: { addon: string }) => {
      if (availableRules.canCustomimzeDesign) {
        return false;
      }

      setUpgradeAccountOpenState({
        key: 'upgradeModal',
        state: true,
        desiredAction: 'customDesign',
        ...(modalData ? { modalData } : {})
      });

      return true;
    },
    [availableRules.canCustomimzeDesign, setUpgradeAccountOpenState]
  );

  const updateDesign = useCallback(
    (patch: Partial<Design>, isOnlyColor?: boolean) => {
      setValue({ design: { ...formValue.design, ...patch } }, isOnlyColor);
    },
    [formValue.design, setValue]
  );

  /**
   * Without custom colours, a palette the old template offered means nothing on the new one,
   * so the new template's first — default — palette comes along with the switch, in the same
   * write. Custom colours are kept as they are.
   */
  const selectTemplate = useCallback(
    (selectedTemplateId: string) => {
      const template = templates.find(({ _id }) => _id === selectedTemplateId);
      const [defaultPreset] = presetsForTemplate(colorPresets, template);

      // Also when the palettes have not loaded yet: the template still switches.
      if (formValue.design.newUseCustomColors || !template || !defaultPreset) {
        setValue({ selectedTemplateId });

        return;
      }

      cancelPendingColorSubmit();
      setValue({
        selectedTemplateId,
        design: {
          ...formValue.design,
          newColorPreset: defaultPreset.id,
          newVariables: { ...formValue.design.newVariables, ...defaultPreset.colors }
        }
      });
    },
    [templates, colorPresets, formValue.design, setValue, cancelPendingColorSubmit]
  );

  const setDesign = useCallback(
    (design: Partial<Design>) => {
      if (design.loadFonts || (design.variables && design.variables.font !== undefined)) {
        const { font } = design.variables || {};

        updateDesign({
          loadFonts: design.loadFonts || [],
          variables: { ...formValue.design.variables, font, headingFont: font }
        });
      }

      // Collected rather than written one by one: each write starts from the same
      // `formValue.design`, so two of them in a row would drop the first one's change.
      const standaloneChanges = STANDALONE_DESIGN_KEYS.reduce<Partial<Design>>((acc, key) => {
        if (design[key] === undefined) {
          return acc;
        }

        return { ...acc, [key]: design[key] };
      }, {});

      const colorVariables = pickColorVariables(design.newVariables);
      const newVariables = { ...formValue.design.newVariables, ...colorVariables };
      const hasStandaloneChanges = Object.keys(standaloneChanges).length > 0;
      const hasColorChanges = Object.keys(colorVariables).length > 0;

      // Picking a palette sets the pinned id and all six colours in one gesture. It is a
      // discrete click rather than a drag, so it persists immediately as a single write —
      // not one request for the id and a second, debounced one for the colours.
      if (hasStandaloneChanges && hasColorChanges) {
        cancelPendingColorSubmit();
        updateDesign({ ...standaloneChanges, newVariables });

        return;
      }

      if (hasStandaloneChanges) {
        updateDesign(standaloneChanges);
      }

      if (hasColorChanges) {
        updateDesign({ newVariables }, true);
      }
    },
    [
      formValue.design.variables,
      formValue.design.newVariables,
      cancelPendingColorSubmit,
      updateDesign
    ]
  );

  const submitDesignColor = useCallback(
    (colors: Variables) => {
      const colorVariables = pickColorVariables(colors);

      if (!Object.keys(colorVariables).length) {
        return;
      }

      updateDesign({ newVariables: { ...formValue.design.newVariables, ...colorVariables } }, true);
      submitDebounced(colorVariables);
    },
    [formValue.design.newVariables, updateDesign, submitDebounced]
  );

  const changeGalleryStyle = useCallback(
    (value: string) => {
      if (value !== DEFAULT_VALUES.galleryStyle && isBlocked()) {
        return;
      }

      setValue({ galleryStyle: value });
    },
    [isBlocked, setValue]
  );

  const toggleDecorations = useCallback(
    (value: boolean) => {
      if (value !== !DEFAULT_VALUES.disableDecorations && isBlocked({ addon: 'template' })) {
        return;
      }

      updateDesign({ disableDecorations: !value });
    },
    [isBlocked, updateDesign]
  );

  const toggleTrademark = useCallback(
    (value: boolean) => updateDesign({ showTrademark: value }),
    [updateDesign]
  );

  return {
    selectTemplate,
    setDesign,
    submitDesignColor,
    changeGalleryStyle,
    toggleDecorations,
    toggleTrademark
  };
};

export default useTemplateConfigurationForm;
